import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { Folder, Hash, KeyRound, LayoutDashboard, Lock, Mail, Settings, ShieldHalf, Star, Trash2, Users, Wand2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useLockVault } from "../app/lockVault.js";
import { paths } from "../app/paths.js";
import { useSession } from "../app/session.js";
import { useMe } from "../data/account.js";
import { useOrgs, usePendingInvites } from "../data/teams.js";
import { useTrashItems, useVaultEntries } from "../data/vault.js";
import { folderCounts } from "../lib/entries.js";
import { Logo } from "../ui/Logo.js";
import { Avatar, IconButton } from "../ui/primitives.js";

interface NavTarget {
  readonly pathname: string;
  readonly search: string;
}

function isActive(current: NavTarget, target: string): boolean {
  const [pathname, query = ""] = target.split("?");
  if (current.pathname !== pathname) {
    return false;
  }
  const wanted = new URLSearchParams(query);
  const have = new URLSearchParams(current.search);
  if (query === "" && (have.get("view") !== null || have.get("folder") !== null)) {
    return false;
  }
  for (const [key, value] of wanted) {
    if (have.get(key) !== value) {
      return false;
    }
  }
  return true;
}

function NavItem(props: {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly count?: number | undefined;
  /** Draws the count as an accent pill, for things that need attention. */
  readonly highlight?: boolean | undefined;
  readonly active: boolean;
  readonly onNavigate?: (() => void) | undefined;
}): ReactNode {
  const Icon = props.icon;
  return (
    <Link
      to={props.to}
      onClick={props.onNavigate}
      aria-current={props.active ? "page" : undefined}
      className={`group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-sm transition-colors duration-150 ${
        props.active ? "bg-foam/[0.07] font-medium text-foam" : "text-mist hover:bg-foam/[0.04] hover:text-foam"
      }`}
    >
      <Icon size={16} aria-hidden="true" className={`shrink-0 ${props.active ? "text-flow" : "text-mist-2 group-hover:text-mist"}`} />
      <span className="min-w-0 flex-1 truncate">{props.label}</span>
      {props.count !== undefined && props.count > 0 ? (
        props.highlight === true ? (
          <span className="grid min-w-5 place-items-center rounded-full bg-flow px-1.5 text-[0.6875rem] font-semibold leading-5 tabular-nums text-ink">{props.count}</span>
        ) : (
          <span className="text-xs tabular-nums text-mist-2">{props.count}</span>
        )
      ) : null}
    </Link>
  );
}

function SectionLabel(props: { readonly children: ReactNode }): ReactNode {
  return <p className="px-2.5 pb-1 pt-5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-mist-2">{props.children}</p>;
}

export function SidebarNav(props: { readonly onNavigate?: (() => void) | undefined; readonly showBrand?: boolean | undefined }): ReactNode {
  const location = useLocation();
  const { state } = useSession();
  const me = useMe();
  const lock = useLockVault();
  const entries = useVaultEntries();
  const trash = useTrashItems();
  const orgs = useOrgs();
  const pendingInvites = usePendingInvites();
  const usableInvites = (pendingInvites.data ?? []).filter((invite): boolean => !invite.stale).length;

  const all = entries.data?.entries ?? [];
  const current: NavTarget = { pathname: location.pathname, search: location.search };
  const folders = folderCounts(all);
  const teams = orgs.data ?? [];

  return (
    <div className="flex h-full flex-col">
      {props.showBrand !== false ? (
        <div className="flex h-14 shrink-0 items-center border-b border-line px-4">
          <Link to={paths.dashboard} onClick={props.onNavigate} aria-label="PasswordFlow – Übersicht" className="rounded-md">
            <Logo />
          </Link>
        </div>
      ) : null}

      <nav aria-label="Hauptnavigation" className="flex min-h-0 flex-1 flex-col gap-px overflow-y-auto px-2.5 py-3">
        <NavItem to={paths.dashboard} label="Übersicht" icon={LayoutDashboard} active={isActive(current, paths.dashboard)} onNavigate={props.onNavigate} />

        <SectionLabel>Tresor</SectionLabel>
        <NavItem to={paths.vault} label="Alle Einträge" icon={KeyRound} count={all.length} active={isActive(current, paths.vault)} onNavigate={props.onNavigate} />
        <NavItem to="/vault?view=favorites" label="Favoriten" icon={Star} count={all.filter((e) => e.meta.favorite).length} active={isActive(current, "/vault?view=favorites")} onNavigate={props.onNavigate} />
        <NavItem to="/vault?view=trash" label="Papierkorb" icon={Trash2} count={trash.data?.length} active={isActive(current, "/vault?view=trash")} onNavigate={props.onNavigate} />

        {folders.length > 0 ? (
          <>
            <SectionLabel>Ordner</SectionLabel>
            {folders.map((folder) => (
              <NavItem
                key={folder.name}
                to={`/vault?${new URLSearchParams({ folder: folder.name }).toString()}`}
                label={folder.name}
                icon={Folder}
                count={folder.count}
                active={isActive(current, `/vault?${new URLSearchParams({ folder: folder.name }).toString()}`)}
                onNavigate={props.onNavigate}
              />
            ))}
          </>
        ) : null}

        <SectionLabel>Werkzeuge</SectionLabel>
        <NavItem to={paths.generator} label="Passwort-Generator" icon={Wand2} active={isActive(current, paths.generator)} onNavigate={props.onNavigate} />

        <SectionLabel>Teams</SectionLabel>
        <NavItem to={paths.teams} label="Alle Teams" icon={Users} count={teams.length} active={location.pathname === paths.teams} onNavigate={props.onNavigate} />
        {usableInvites > 0 ? (
          <NavItem
            to={paths.teams}
            label="Einladungen"
            icon={Mail}
            count={usableInvites}
            highlight
            active={false}
            onNavigate={props.onNavigate}
          />
        ) : null}
        {teams.map((team) => (
          <NavItem
            key={team.id}
            to={paths.team(team.id)}
            label={team.name}
            icon={Hash}
            active={location.pathname === paths.team(team.id)}
            onNavigate={props.onNavigate}
          />
        ))}
      </nav>

      <div className="shrink-0 border-t border-line p-2.5">
        {me.data?.role === "admin" ? (
          <NavItem to={paths.admin} label="Verwaltung" icon={ShieldHalf} active={isActive(current, paths.admin)} onNavigate={props.onNavigate} />
        ) : null}
        <NavItem to={paths.settings} label="Einstellungen" icon={Settings} active={isActive(current, paths.settings)} onNavigate={props.onNavigate} />
        <div className="mt-2 flex items-center gap-2.5 rounded-lg border border-line bg-ink-2 py-2 pl-2.5 pr-1">
          <Avatar label={state.status === "locked" ? "?" : state.email} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[0.8125rem] font-medium text-foam" title={state.status === "locked" ? undefined : state.email}>
              {state.status === "locked" ? "Gesperrt" : state.email}
            </p>
            <p className="flex items-center gap-1.5 text-[0.7rem] text-mist-2">
              <span className={`size-1.5 rounded-full ${state.status === "expired" ? "bg-buoy" : "bg-flow"}`} aria-hidden="true" />
              {state.status === "expired" ? "Sitzung abgelaufen" : "Entsperrt"}
            </p>
          </div>
          <IconButton
            label="Tresor sperren"
            icon={Lock}
            size="sm"
            onClick={(): void => {
              props.onNavigate?.();
              void lock("manual");
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function Sidebar(): ReactNode {
  return (
    <aside className="sticky top-0 hidden h-dvh w-[248px] shrink-0 border-r border-line bg-ink lg:block">
      <SidebarNav />
    </aside>
  );
}
